import { clampSidebarWidth, DESKTOP_SIDEBAR_MAX_WIDTH } from "./sidebar_width";
import { DESKTOP_SIDEBAR_DEFAULT_WIDTH } from "../components/header/header_styled_components";

// One key for the whole sidebar. It used to be five — expanded, hidden, width and a flag
// per collapsible section — which is a lot of top-level noise in a store shared with
// every other feature, for settings that always travel together.
const STORAGE_KEY = "sidebar";

export interface SidebarState {
	/** The panel is open rather than showing labels only. */
	expanded: boolean;
	/** The panel is collapsed into the icon rail. */
	collapsed: boolean;
	width: number;
	profileExpanded: boolean;
	adminExpanded: boolean;
}

const DEFAULTS: SidebarState = {
	expanded: true,
	collapsed: false,
	width: DESKTOP_SIDEBAR_DEFAULT_WIDTH,
	profileExpanded: true,
	adminExpanded: true,
};

// The width is stored as `panelWidth`. The old `width` field was written on every first
// visit, so its 272 — then both the default and the maximum — is almost always nobody's
// choice; only a value someone actually dragged to is carried over.
const LEGACY_DEFAULT_WIDTH = DESKTOP_SIDEBAR_MAX_WIDTH;

const bool = (value: unknown, fallback: boolean): boolean =>
	typeof value === "boolean" ? value : fallback;

const isNumber = (value: unknown): value is number =>
	typeof value === "number" && !Number.isNaN(value);

const width = (stored: { panelWidth?: unknown; width?: unknown }): number => {
	if (isNumber(stored.panelWidth)) return clampSidebarWidth(stored.panelWidth);
	if (isNumber(stored.width) && stored.width !== LEGACY_DEFAULT_WIDTH) {
		return clampSidebarWidth(stored.width);
	}
	return DEFAULTS.width;
};

const serialize = ({ width: panelWidth, ...rest }: SidebarState) => ({ ...rest, panelWidth });

// The store holds whatever an older build, another tab or the user left there, and
// reading it throws outright where site data is blocked. Every field falls back on its
// own, so one bad value does not cost the other four.
const parse = (raw: string | null): SidebarState => {
	if (raw === null) return DEFAULTS;
	try {
		const stored = JSON.parse(raw);
		if (typeof stored !== "object" || stored === null) return DEFAULTS;
		return {
			expanded: bool(stored.expanded, DEFAULTS.expanded),
			collapsed: bool(stored.collapsed, DEFAULTS.collapsed),
			width: width(stored),
			profileExpanded: bool(stored.profileExpanded, DEFAULTS.profileExpanded),
			adminExpanded: bool(stored.adminExpanded, DEFAULTS.adminExpanded),
		};
	} catch {
		return DEFAULTS;
	}
};

export const readSidebarState = (): SidebarState => {
	try {
		return parse(localStorage.getItem(STORAGE_KEY));
	} catch {
		return DEFAULTS;
	}
};

/** Writes one field, merging into what is stored. The sections' flags are held by two
 *  components at once (the sidebar and the tablet menu), each with its own state, so a
 *  write of the whole object would let them overwrite each other's fields — which is
 *  exactly what separate keys used to prevent. */
export const writeSidebarField = <K extends keyof SidebarState>(
	field: K,
	value: SidebarState[K],
): void => {
	try {
		const next = { ...readSidebarState(), [field]: value };
		localStorage.setItem(STORAGE_KEY, JSON.stringify(serialize(next)));
	} catch {
		// A full or blocked store is not worth failing the interaction over.
	}
};
